import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Sparkles, Shield, Zap, Download, Share, Plus, X, ExternalLink } from 'lucide-react';
import RexLogo from './RexLogo';
import TermsModal from './TermsModal';

// Heuristics to pick the right install path on each device.
function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && (navigator.maxTouchPoints || 0) > 1);
  const isAndroid = /Android/.test(ua);
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (isStandalone) return 'installed';
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
}

export default function Landing({ onStart }) {
  const [platform, setPlatform] = useState('other');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    function onInstalled() {
      setPlatform('installed');
      setDeferredPrompt(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const onInstallClick = useCallback(async () => {
    if (deferredPrompt) {
      // Chrome / Edge / Android Chrome — native install prompt available
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') setPlatform('installed');
        setDeferredPrompt(null);
        return;
      } catch (e) {
        console.warn('Install prompt failed', e);
      }
    }
    // Fallback: open the how-to modal with platform-specific instructions
    setHowtoOpen(true);
  }, [deferredPrompt]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--cream)' }} data-testid="landing">
      {/* HERO */}
      <main className="flex-1 flex items-center px-5 sm:px-8 md:px-12 py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto w-full grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* LEFT — logo on a tag */}
          <div className="order-1 md:order-2 flex justify-center md:justify-end">
            <div className="relative">
              <div
                className="absolute -inset-6 opacity-30"
                aria-hidden="true"
                style={{
                  backgroundImage: 'radial-gradient(var(--ink) 1px, transparent 1.5px)',
                  backgroundSize: '14px 14px',
                  maskImage: 'radial-gradient(closest-side, black, transparent)',
                  WebkitMaskImage: 'radial-gradient(closest-side, black, transparent)',
                }}
              />
              <div className="relative rex-wiggle" data-testid="landing-rex">
                <RexLogo size={300} className="md:!w-[360px]" />
              </div>
              <div
                className="absolute -top-4 -left-4 rotate-[-8deg] px-3 py-1 font-mono text-[10px] tracking-widest"
                style={{ background: 'var(--amber)', color: 'var(--ink)', border: '2px solid var(--ink)', boxShadow: '3px 3px 0 var(--ink)' }}
              >
                v2.2 LIVE
              </div>
            </div>
          </div>

          {/* RIGHT — copy + CTA */}
          <div className="order-2 md:order-1">
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.35em] mb-4 sm:mb-6" style={{ color: 'var(--amber-2)' }}>
              INTRODUCING
            </p>
            <h1
              className="font-display leading-[0.92]"
              style={{
                color: 'var(--ink)',
                fontSize: 'clamp(2.4rem, 9vw, 5.4rem)',
              }}
              data-testid="landing-title"
            >
              CROPASAURUS<br />REX
            </h1>
            <p className="font-condensed mt-5 sm:mt-7 text-xl sm:text-2xl md:text-3xl leading-tight max-w-md" style={{ color: 'var(--moss)' }}>
              The mass auto-cropping tool for busy humans who actually get stuff done.
            </p>
            <p className="font-mono text-xs sm:text-sm mt-4 sm:mt-5 max-w-md tracking-wide" style={{ color: 'var(--moss)' }}>
              Feed it a sticker, badge or icon sheet. The Rex slices it into a pack of crisp,
              transparent PNGs. No subscriptions. No confusion. No faff.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={onStart}
                className="btn-amber inline-flex items-center gap-3 text-base sm:text-lg !px-7 !py-4"
                data-testid="start-cropping-btn"
              >
                START CROPPING <ArrowRight size={20} />
              </button>

              {platform === 'installed' ? (
                <span
                  className="font-mono text-[11px] tracking-widest inline-flex items-center gap-2 px-3 py-2"
                  style={{ background: 'var(--ink)', color: 'var(--amber)', border: '2px solid var(--ink)' }}
                  data-testid="installed-pill"
                >
                  ✓ INSTALLED AS APP
                </span>
              ) : (
                <button
                  type="button"
                  onClick={onInstallClick}
                  className="btn-ink inline-flex items-center gap-2 text-sm sm:text-base"
                  data-testid="install-app-btn"
                  title="Save Cropasaurus Rex to your home screen as an app"
                >
                  <Download size={18} /> ADD TO HOME SCREEN
                </button>
              )}
            </div>
            <p className="font-mono text-[11px] tracking-widest mt-3" style={{ color: 'var(--moss)' }}>
              ↳ no signup · runs in your browser · works offline once installed
            </p>

            {/* Pillars row */}
            <div className="mt-10 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
              {[
                { I: Zap, t: 'FAST', s: '6 icons in 3s' },
                { I: Shield, t: 'PRIVATE', s: 'never leaves device' },
                { I: Sparkles, t: 'PRETTY', s: 'transparent PNGs' },
              ].map(({ I, t, s }) => (
                <div key={t} className="border-2 p-3 sm:p-4" style={{ borderColor: 'var(--ink)' }}>
                  <I size={18} style={{ color: 'var(--amber-2)' }} />
                  <p className="font-condensed text-base sm:text-lg mt-2 tracking-wide" style={{ color: 'var(--ink)' }}>{t}</p>
                  <p className="font-mono text-[10px] sm:text-[11px] mt-1 opacity-70" style={{ color: 'var(--moss)' }}>{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* CREATED-BY band */}
      <section className="px-5 sm:px-8 md:px-12 py-10 md:py-14" style={{ background: 'var(--cream-2)', borderTop: '2px solid var(--ink)' }} data-testid="landing-credits">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row md:items-end gap-6 md:gap-12">
          <div className="flex-1">
            <h2 className="font-condensed text-3xl sm:text-4xl md:text-5xl leading-none" style={{ color: 'var(--ink)' }}>
              CREATED BY<br />A LEGEND
            </h2>
            <p className="font-mono text-xs sm:text-sm mt-4 tracking-widest" style={{ color: 'var(--moss)' }}>
              Built with care for the maker who has 200 stickers to crop and 20 minutes to do it in.
            </p>
          </div>

          <div className="flex-1">
            <div className="py-4 px-5 font-display text-base sm:text-lg tracking-widest text-center"
                 style={{ background: 'var(--ink)', color: 'var(--paper)' }}>
              PROPERTY OF HERITAGE DAHLIAS NZ LIMITED
            </div>
            <p className="font-mono text-[10px] sm:text-xs mt-3 tracking-widest text-center" style={{ color: 'var(--moss)' }}>
              © 2026 · ALL RIGHTS RESERVED · CROPASAURUSREX.NZ
            </p>

            {/* Sister-site links */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3" data-testid="sister-links">
              <a
                href="https://heritagedahlias.nz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-widest transition-colors"
                style={{ border: '2px solid var(--ink)', color: 'var(--ink)' }}
                data-testid="link-heritagedahlias"
              >
                HERITAGEDAHLIAS.NZ <ExternalLink size={12} />
              </a>
              <a
                href="https://heritagebloomclub.nz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-widest transition-colors"
                style={{ border: '2px solid var(--ink)', color: 'var(--ink)' }}
                data-testid="link-bloomclub"
              >
                HERITAGEBLOOMCLUB.NZ <ExternalLink size={12} />
              </a>
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-widest"
                style={{ background: 'var(--ink)', color: 'var(--amber)' }}
                data-testid="open-terms-landing"
              >
                TERMS &amp; POLICIES
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="zigzag-top" />

      {/* INSTALL HOW-TO MODAL */}
      {howtoOpen && (
        <HowToInstallModal platform={platform} onClose={() => setHowtoOpen(false)} />
      )}

      {/* TERMS MODAL */}
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}

function HowToInstallModal({ platform, onClose }) {
  const tabs = [
    { id: 'ios', label: 'IPHONE / IPAD' },
    { id: 'android', label: 'ANDROID' },
    { id: 'desktop', label: 'DESKTOP' },
  ];
  const initialTab = platform === 'ios' || platform === 'android' || platform === 'desktop' ? platform : 'ios';
  const [tab, setTab] = useState(initialTab);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(31,61,43,0.78)' }}
      onClick={onClose}
      data-testid="install-howto-modal"
    >
      <div
        className="max-w-lg w-full"
        style={{ background: 'var(--cream)', border: '3px solid var(--ink)', boxShadow: '8px 8px 0 var(--amber)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '2px solid var(--ink)' }}>
          <h3 className="font-display text-lg tracking-wider" style={{ color: 'var(--ink)' }}>
            ADD TO HOME SCREEN
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center"
            style={{ background: 'var(--ink)', color: 'var(--cream)' }}
            data-testid="close-howto"
            aria-label="Close"
          ><X size={16} /></button>
        </div>

        {/* tabs */}
        <div className="flex" style={{ borderBottom: '2px solid var(--ink)' }} role="tablist">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              onClick={() => setTab(t.id)}
              className="flex-1 py-3 font-mono text-[11px] tracking-widest transition-colors"
              style={{
                background: tab === t.id ? 'var(--ink)' : 'transparent',
                color: tab === t.id ? 'var(--amber)' : 'var(--ink)',
              }}
              data-testid={`howto-tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>

        {/* content */}
        <div className="px-5 py-5 max-h-[55vh] overflow-y-auto">
          {tab === 'ios' && (
            <ol className="font-mono text-[13px] leading-relaxed space-y-3" style={{ color: 'var(--ink)' }}>
              <li className="flex gap-3">
                <span className="font-condensed text-amber-700" style={{ color: 'var(--amber-2)' }}>1.</span>
                <span>Open this page in <b>Safari</b> (it won&apos;t work from in-app browsers like Instagram or Gmail).</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>2.</span>
                <span className="flex-1 inline-flex flex-wrap items-center gap-2">
                  Tap the <Share size={16} style={{ display: 'inline', verticalAlign: 'middle' }} /> <b>Share</b> button at the bottom of the screen.
                </span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>3.</span>
                <span className="flex-1 inline-flex flex-wrap items-center gap-2">
                  Scroll down and tap <Plus size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> <b>Add to Home Screen</b>.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>4.</span>
                <span>Tap <b>Add</b> in the top right. Cropasaurus Rex now lives on your home screen with the green tortoise icon.</span>
              </li>
            </ol>
          )}

          {tab === 'android' && (
            <ol className="font-mono text-[13px] leading-relaxed space-y-3" style={{ color: 'var(--ink)' }}>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>1.</span>
                <span>Open this page in <b>Chrome</b> (or any Chromium-based browser).</span>
              </li>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>2.</span>
                <span>Tap the <b>⋮ menu</b> in the top right.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>3.</span>
                <span>Tap <b>Install app</b> (or <b>Add to home screen</b>).</span>
              </li>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>4.</span>
                <span>Confirm. The green tortoise icon will appear in your app drawer like any other app.</span>
              </li>
              <li className="font-mono text-[11px] opacity-70 pt-2">
                ↳ Tip: if Chrome offered you the install prompt automatically, the <b>ADD TO HOME SCREEN</b> button on this page will trigger it directly.
              </li>
            </ol>
          )}

          {tab === 'desktop' && (
            <ol className="font-mono text-[13px] leading-relaxed space-y-3" style={{ color: 'var(--ink)' }}>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>1.</span>
                <span>In <b>Chrome</b> or <b>Edge</b>, look for the <b>⬇ install icon</b> at the right end of the address bar.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>2.</span>
                <span>Click it, then click <b>Install</b>. Cropasaurus Rex opens in its own window — like a native app.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-condensed" style={{ color: 'var(--amber-2)' }}>3.</span>
                <span>It also pins to your taskbar / dock with the green tortoise icon.</span>
              </li>
              <li className="font-mono text-[11px] opacity-70 pt-2">
                ↳ On Safari macOS use <b>File → Add to Dock</b> (Sonoma 14+) for the same result.
              </li>
            </ol>
          )}
        </div>

        <div className="px-5 py-4" style={{ borderTop: '2px solid var(--ink)', background: 'var(--cream-2)' }}>
          <p className="font-mono text-[11px] tracking-widest opacity-70" style={{ color: 'var(--moss)' }}>
            Once installed, Cropasaurus Rex works fully offline.
          </p>
        </div>
      </div>
    </div>
  );
}

